import * as React from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { cn } from '@klear/ui/lib/utils'
import omit from 'lodash/omit'
import toArray from 'lodash/toArray'

interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  className?: string
  onChange?: (files: FileList | null) => void
  value?: FileList | null
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ className, onChange, ...props }, forwardedRef) => {
    const localRef = React.useRef<HTMLInputElement>(null)
    const inputRef = (forwardedRef as React.RefObject<HTMLInputElement>) || localRef
    const [files, setFiles] = React.useState<FileList | null>(null)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = e.target.files
      setFiles(newFiles)
      onChange?.(newFiles)
    }

    const clearFiles = () => {
      if (inputRef.current) {
        inputRef.current.value = ''
        setFiles(null)
        onChange?.(null)
      }
    }

    return (
      <>
        <div className="flex items-center gap-2">
          <input
            type="file"
            className={cn(
              'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm cursor-pointer',
              className
            )}
            ref={inputRef}
            onChange={handleChange}
            {...omit(props, 'value')}
          />
          {files && files.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={clearFiles}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        {files && files.length > 0 && (
          <div className="m-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {toArray(files).map((file: File) => (
              <div key={file.name}>{file.name}</div>
            ))}
          </div>
        )}
      </>
    )
  }
)

FileInput.displayName = 'FileInput'

export { FileInput }
